import type { GeneratedReport, ReportType } from '../types/ai'
import type { ApplicationRecord } from '../types/domain'
import { STAGE_META, summariseGate } from './stages'
import { scoreBand, bandLabel } from './scoring'
import { formatAed } from './format'

export const reportTypeLabels: Record<ReportType, string> = {
  'application-assessment': 'Application assessment',
  'bid-readiness': 'Bid readiness review',
  'stage-gate-review': 'Stage-gate review',
  'portfolio-status': 'Portfolio status',
  'outreach-performance': 'Outreach performance'
}

interface BuildInput {
  type: ReportType
  scopeRecords: ApplicationRecord[]
  scopeLabel: string
  period: string
  audience: string
  author: string
}

export function buildReport({
  type,
  scopeRecords,
  scopeLabel,
  period,
  audience,
  author
}: BuildInput): GeneratedReport {
  const live = scopeRecords.filter((record) => record.status !== 'rejected')
  const held = live.filter((record) => {
    const summary = summariseGate(record.gates[record.stage] ?? [])
    return summary.total > 0 && !summary.cleared
  })
  const committed = live.reduce((sum, record) => sum + record.requestedSupportAed, 0)
  const criticalFlags = live.flatMap((record) =>
    record.flags.filter((flag) => flag.severity === 'critical').map((flag) => ({ record, flag }))
  )

  const scoredRows = live.slice(0, 8).map((record) => ({
    subject: record.eventName,
    score: record.aiScore,
    band: bandLabel(scoreBand(record.aiScore)),
    note: `${STAGE_META[record.stage].label} · ${
      summariseGate(record.gates[record.stage] ?? []).percent
    }% gate readiness`
  }))

  const findings = [
    {
      id: 'gf-1',
      label: `${held.length} of ${live.length} records are holding at their current gate`,
      detail: held.length
        ? `Binding constraints are concentrated on ${held
            .slice(0, 3)
            .map((record) => STAGE_META[record.stage].label)
            .join(', ')} gates, where mandatory artefacts or sign-offs remain open.`
        : 'All in-scope records have cleared their current gate and are recommended to advance.'
    },
    {
      id: 'gf-2',
      label: `${criticalFlags.length} critical risk flag${criticalFlags.length === 1 ? '' : 's'} open`,
      detail: criticalFlags.length
        ? criticalFlags
            .slice(0, 2)
            .map(({ record, flag }) => `${record.ref}: ${flag.label}`)
            .join('; ')
        : 'No critical risk flags are open across the reporting scope.'
    },
    {
      id: 'gf-3',
      label: `${formatAed(committed)} of support in scope`,
      detail: `Across ${live.length} live records, with a mean weighted AI score of ${(
        live.reduce((sum, record) => sum + record.aiScore, 0) / Math.max(live.length, 1)
      ).toFixed(1)}.`
    }
  ]

  const recommendations = live
    .flatMap((record) =>
      record.recommendations
        .filter((recommendation) => recommendation.status === 'pending')
        .map((recommendation) => `${record.ref}: ${recommendation.title}`)
    )
    .slice(0, 5)

  return {
    id: `rp-${Math.random().toString(36).slice(2, 9)}`,
    title: `${reportTypeLabels[type]} — ${scopeLabel}`,
    type,
    scope: scopeLabel,
    period,
    audience,
    author,
    createdOn: new Date().toISOString().slice(0, 10),
    status: 'ready',
    executiveSummary: `This ${reportTypeLabels[
      type
    ].toLowerCase()} covers ${live.length} record${live.length === 1 ? '' : 's'} for ${period}. ${
      held.length
    } record${held.length === 1 ? '' : 's'} are holding at their current gate and ${
      criticalFlags.length
    } critical risk flag${criticalFlags.length === 1 ? '' : 's'} are open. Total support in scope is ${formatAed(
      committed
    )}. All scoring is advisory: every criterion is evidence-linked and every recommendation can be accepted or overridden by the assigned officer with a recorded rationale.`,
    findings,
    scoredRows,
    recommendations:
      recommendations.length > 0
        ? recommendations
        : ['No open recommendations across the reporting scope.'],
    appendix: [
      'Criterion-level scoring with evidence citations',
      'Gate status by record and stage',
      'Recommendation rule triggers and officer decisions'
    ]
  }
}
